import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { EditorModule } from 'primeng/editor';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { CommentsAttachmentsService } from './comments-attachments.service';
import { CommentsService } from '../services/comments.service';
import { CommentItemComponent } from './comment-item/comment-item.component';
import {
  CommentThreadNode,
  CommentWithUser,
  CreateCommentRequest,
  EntityType,
} from './comments.models';
import { MentionInputComponent } from './mention-input/mention-input.component';

/** Pending file in the composer with a blob URL for thumbnails / preview */
interface ComposerAttachment {
  file: File;
  previewUrl: string;
}

@Component({
  selector: 'app-comments-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextareaModule,
    CheckboxModule,
    DialogModule,
    TooltipModule,
    ToastModule,
    ProgressSpinnerModule,
    CommentItemComponent,
    MentionInputComponent,
    EditorModule
  ],
  providers: [MessageService],
  templateUrl: './comments-panel.component.html',
  styleUrl: './comments-panel.component.scss',
  animations: [
    trigger('slidePanel', [
      state('true', style({ right: '0px' })),
      state('false', style({ right: '-450px' })),
      transition('false => true', animate('300ms ease-out')),
      transition('true => false', animate('300ms ease-in')),
    ]),
    trigger('overlay', [
      state('true', style({ opacity: 1, display: 'block' })),
      state('false', style({ opacity: 0, display: 'none' })),
      transition('false => true', animate('200ms')),
      transition('true => false', animate('200ms')),
    ]),
  ],
})
export class CommentsPanelComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() commentCountChange = new EventEmitter<number>();
  @Input() inlineMode: boolean = false;
  @Input() workQueue: boolean;

  @Input() entityType!: EntityType;
  @Input() entityId!: string;
  @Input() attachmentTags: string[] = [];

  @ViewChild('panelBody') panelBody?: ElementRef<HTMLDivElement>;
  @ViewChild('newCommentInput') newCommentInput?: MentionInputComponent;
  @ViewChild('replyInput') replyInput?: MentionInputComponent;

  comments: CommentWithUser[] = [];
  /** Top-level threads; each node may have nested replies. */
  threadedComments: CommentThreadNode[] = [];
  loading = false;
  editorModules: Record<string, unknown>;

  // New comment form (p-editor may set null when empty)
  newCommentText: string | null = '';
  isInternal = true;
  isSubmitting = false;

  // Reply form
  replyToComment: CommentWithUser | null = null;
  replyText: string | null = '';
  isReplySubmitting = false;
  newCommentAttachments: ComposerAttachment[] = [];
  replyAttachments: ComposerAttachment[] = [];

  previewDialogVisible = false;
  previewAttachment: ComposerAttachment | null = null;

  // Current user info
  currentUserId?: number;
  isAdmin = false;

  private needsScrollToBottom = false;

  constructor(
    private commentsService: CommentsService,
    private commentsAttachmentsService: CommentsAttachmentsService,
    private messageService: MessageService,
    public appService: AppService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnDestroy(): void {
    this.revokeAllAttachmentUrls();
  }

  ngOnInit(): void {
    console.log(this.workQueue, 'workQueueworkQueueworkQueueworkQueue')
    this.loadCurrentUserInfo();
    this.editorModules = {
      mention: {
        // Append menu to document.body + position:fixed — avoids clipping by
        // .comments-panel overflow and tab scroll areas; enables scroll when tall.
        positioningStrategy: 'fixed',
        allowedChars: /^[A-Za-z0-9\s]*$/,
        mentionDenotationChars: ['@'],
        source: (searchTerm: string, renderList: (values: { id: number; value: string }[], term: string) => void) => {
          this.commentsService.searchUsers(searchTerm).subscribe((users) => {
            const values = users.map((user) => ({
              id: user.id,
              value: `${user.first_name} ${user.last_name}`,
            }));
            renderList(values, searchTerm);
          });
        },
      },
    };
  }

  ngAfterViewInit(): void {
    // Scroll to bottom when view is initialized
    if (this.visible) {
      this.needsScrollToBottom = true;
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsScrollToBottom) {
      this.needsScrollToBottom = false;
      this.scrollToBottom();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.entityType || !this.entityId) {
      return;
    }
    if (!this.visible) {
      return;
    }
    // Reload when opening the panel, or when the bound entity changes (e.g. inline task detail).
    const visibleOpened = changes['visible']?.currentValue === true;
    const entityIdChanged = changes['entityId'] && changes['entityId'].currentValue !== changes['entityId'].previousValue;
    const entityTypeChanged = changes['entityType'] && changes['entityType'].currentValue !== changes['entityType'].previousValue;
    if (visibleOpened || entityIdChanged || entityTypeChanged) {
      this.loadComments();
    }
  }

  /**
   * Load current user information from localStorage
   */
  loadCurrentUserInfo(): void {
    try {
      const userInfo = JSON.parse(localStorage.getItem('optplanner_user') || '{}');
      this.currentUserId = userInfo.id;
      this.isAdmin = userInfo.is_super_admin || false;
    } catch (error) {
      console.error('Failed to load user info from localStorage:', error);
      this.currentUserId = undefined;
      this.isAdmin = false;
    }
  }

  /**
   * Load comments for the entity.
   * Pass silent=true (e.g. after posting) to refresh the list in the background
   * without hiding the existing comments behind a spinner.
   */
  loadComments(silent = false): void {
    if (!silent) {
      this.loading = true;
      this.cdr.detectChanges();
    }

    this.commentsService
      .getCommentsForEntity(
        this.entityType,
        this.entityId,
        true,
        true
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (comments) => {
          this.comments = comments;
          this.commentCountChange.emit(
            comments.filter((comment) => !comment.is_deleted).length
          );
          this.rebuildCommentThreads();
          this.needsScrollToBottom = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to load comments:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load comments',
          });
        },
      });
  }

  /**
   * Scroll panel body to bottom
   */
  private scrollToBottom(): void {
    if (this.panelBody) {
      const element = this.panelBody.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  /**
   * Extract mentioned user IDs from comment text.
   * Uses mention-input when present; otherwise parses p-editor HTML (quill-mention
   * embeds spans with data-id) and plain @123 tokens for compatibility.
   */
  private extractMentions(text: string, mentionComponent?: MentionInputComponent): number[] {
    if (mentionComponent) {
      return mentionComponent.getMentionedUserIds();
    }
    return this.extractMentionsFromRichOrPlainText(text);
  }

  private extractMentionsFromRichOrPlainText(text: string): number[] {
    const fromSpans = this.extractUserIdsFromQuillMentionHtml(text);
    const fromPlain = this.extractUserIdsFromPlainAtMentions(text);
    return [...new Set([...fromSpans, ...fromPlain])];
  }

  /** quill-mention stores selected users on span.mention as data-id */
  private extractUserIdsFromQuillMentionHtml(html: string): number[] {
    if (!html || !html.includes('mention')) {
      return [];
    }
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const ids: number[] = [];
      doc.querySelectorAll('.mention[data-id]').forEach((el) => {
        const raw = el.getAttribute('data-id');
        if (raw) {
          const userId = parseInt(raw, 10);
          if (!isNaN(userId)) {
            ids.push(userId);
          }
        }
      });
      return ids;
    } catch {
      return [];
    }
  }

  private extractUserIdsFromPlainAtMentions(text: string): number[] {
    const mentionPattern = /@(\d+)/g;
    const mentionedUserIds: number[] = [];
    let match: RegExpExecArray | null;
    while ((match = mentionPattern.exec(text)) !== null) {
      const userId = parseInt(match[1], 10);
      if (!isNaN(userId)) {
        mentionedUserIds.push(userId);
      }
    }
    return mentionedUserIds;
  }

  /**
   * Close the panel
   */
  close(): void {
    if (this.inlineMode) {
      return;
    }
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForms();
  }

  /**
   * Reset all forms
   */
  resetForms(): void {
    this.newCommentText = '';
    this.isInternal = false;
    this.replyToComment = null;
    this.replyText = '';
    this.previewDialogVisible = false;
    this.previewAttachment = null;
    this.revokeAllAttachmentUrls();
    this.newCommentAttachments = [];
    this.replyAttachments = [];
  }

  /**
   * Create a new top-level comment
   */
  createComment(): void {
    const body = this.trimmedEditorHtml(this.newCommentText);
    if (!body) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter a comment',
      });
      return;
    }

    this.isSubmitting = true;
    this.appService.showCustomLoader(true);
    const request: CreateCommentRequest = {
      entity_type: this.entityType,
      entity_id: this.entityId,
      comment_text: body,
      is_internal_only: this.isInternal,
      mentioned_user_ids: this.extractMentions(this.newCommentText ?? '', this.newCommentInput),
    };

    const pendingFiles = this.newCommentAttachments.map((a) => a.file);
    this.commentsService.createComment(request).subscribe({
      next: async (response) => {
        try {
          await this.uploadAttachmentsIfNeeded(response.comment_id, pendingFiles);
        } catch {
          // Keep comment creation successful even if upload fails unexpectedly.
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Comment created successfully',
        });
        this.appService.showCustomLoader(false);
        this.newCommentText = '';
        this.revokeUrls(this.newCommentAttachments);
        this.newCommentAttachments = [];
        // this.isInternal = false;
        this.isSubmitting = false;
        this.cdr.detectChanges();
        this.loadComments(true); // silent refresh — keep existing list visible
      },
      error: (error) => {
        console.error('Failed to create comment:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create comment',
        });
        this.appService.showCustomLoader(false);
        this.isSubmitting = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Handle reply button click from comment item
   */
  onReplyClicked(comment: CommentWithUser): void {
    this.revokeUrls(this.replyAttachments);
    this.replyAttachments = [];
    this.replyToComment = comment;
    this.replyText = '';

    // Scroll to reply form
    setTimeout(() => {
      const replyForm = document.querySelector('.reply-form');
      if (replyForm) {
        replyForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  }

  /**
   * Cancel reply
   */
  cancelReply(): void {
    this.revokeUrls(this.replyAttachments);
    this.replyAttachments = [];
    this.replyToComment = null;
    this.replyText = '';
  }

  /**
   * Submit reply
   */
  submitReply(): void {
    const body = this.trimmedEditorHtml(this.replyText);
    if (!body || !this.replyToComment) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter a reply',
      });
      return;
    }

    this.isReplySubmitting = true;
    this.appService.showCustomLoader(true);
    const request: CreateCommentRequest = {
      entity_type: this.entityType,
      entity_id: this.entityId,
      comment_text: body,
      parent_comment_id: this.replyToComment.id,
      is_internal_only: this.isInternal,
      mentioned_user_ids: this.extractMentions(this.replyText ?? '', this.replyInput),
    };

    const pendingFiles = this.replyAttachments.map((a) => a.file);
    this.commentsService.createComment(request).subscribe({
      next: async (response) => {
        try {
          await this.uploadAttachmentsIfNeeded(response.comment_id, pendingFiles);
        } catch {
          // Keep reply creation successful even if upload fails unexpectedly.
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Reply added successfully',
        });
        this.appService.showCustomLoader(false);
        this.replyToComment = null;
        this.replyText = '';
        this.revokeUrls(this.replyAttachments);
        this.replyAttachments = [];
        this.isReplySubmitting = false;
        this.cdr.detectChanges();
        this.loadComments(true); // silent refresh — keep existing list visible
      },
      error: (error) => {
        console.error('Failed to create reply:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create reply',
        });
        this.appService.showCustomLoader(false);
        this.isReplySubmitting = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Handle comment updated event
   */
  onCommentUpdated(): void {
    this.loadComments();
  }

  onNewCommentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const added = Array.from(input.files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    this.newCommentAttachments = [...this.newCommentAttachments, ...added];
    input.value = '';
  }

  onReplyFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const added = Array.from(input.files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    this.replyAttachments = [...this.replyAttachments, ...added];
    input.value = '';
  }

  removeNewCommentAttachment(index: number): void {
    const removed = this.newCommentAttachments[index];
    if (removed) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.newCommentAttachments = this.newCommentAttachments.filter((_, i) => i !== index);
  }

  removeReplyAttachment(index: number): void {
    const removed = this.replyAttachments[index];
    if (removed) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.replyAttachments = this.replyAttachments.filter((_, i) => i !== index);
  }

  openAttachmentPreview(att: ComposerAttachment): void {
    this.previewAttachment = att;
    this.previewDialogVisible = true;
  }

  onPreviewDialogHide(): void {
    this.previewAttachment = null;
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  getFileExtension(fileName: string): string {
    const i = fileName.lastIndexOf('.');
    if (i < 0 || i === fileName.length - 1) {
      return 'FILE';
    }
    return fileName.slice(i + 1, i + 5).toUpperCase();
  }

  previewPdfSrc(): SafeResourceUrl | null {
    if (!this.previewAttachment || !this.isPdfFile(this.previewAttachment.file)) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.previewAttachment.previewUrl);
  }

  private revokeUrls(list: ComposerAttachment[]): void {
    list.forEach((a) => URL.revokeObjectURL(a.previewUrl));
  }

  private revokeAllAttachmentUrls(): void {
    this.revokeUrls(this.newCommentAttachments);
    this.revokeUrls(this.replyAttachments);
  }

  private async uploadAttachmentsIfNeeded(commentId: number, files: File[]): Promise<void> {
    if (!files.length) return;

    const summary = await firstValueFrom(
      this.commentsAttachmentsService.uploadCommentAttachments(commentId, files, this.attachmentTags)
    );

    if (summary.failureCount > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Partial Upload',
        detail: `${summary.successCount} uploaded, ${summary.failureCount} failed`,
      });
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Attachments Uploaded',
      detail: `${summary.successCount} attachment${summary.successCount === 1 ? '' : 's'} uploaded`,
    });
  }

   //Handle comment deleted event — reload so deleted parents with replies show as placeholders.
  onCommentDeleted(_commentId: number): void {
    this.loadComments(true);
  }

   //Group flat comments into a parent → replies tree using parent_comment_id.

  private rebuildCommentThreads(): void {
    this.threadedComments = this.buildCommentThreads(this.getDisplayComments());
  }

  //Non-deleted comments plus deleted comments that still have active replies beneath them.
  private getDisplayComments(): CommentWithUser[] {
    return this.filterCommentsWithReplies(this.comments);
  }

  private filterCommentsWithReplies(comments: CommentWithUser[]): CommentWithUser[] {
    const hasActiveDescendant = (commentId: number, visited = new Set<number>()): boolean => {
      if (visited.has(commentId)) {
        return false;
      }
      visited.add(commentId);
      for (const c of comments) {
        if (c.parent_comment_id !== commentId) {
          continue;
        }
        if (!c.is_deleted) {
          return true;
        }
        if (hasActiveDescendant(c.id, visited)) {
          return true;
        }
      }
      return false;
    };

    return comments.filter((c) => !c.is_deleted || hasActiveDescendant(c.id));
  }

  private buildCommentThreads(comments: CommentWithUser[]): CommentThreadNode[] {
    const byId = new Map<number, CommentThreadNode>();
    const roots: CommentThreadNode[] = [];

    const sorted = [...comments].sort(
      (a, b) => this.compareCommentDates(a.created_at, b.created_at)
    );

    for (const comment of sorted) {
      byId.set(comment.id, { comment, replies: [] });
    }

    for (const comment of sorted) {
      const node = byId.get(comment.id);
      if (!node) {
        continue;
      }
      const parentId = comment.parent_comment_id;
      if (parentId != null && byId.has(parentId)) {
        byId.get(parentId)!.replies.push(node);
      } else {
        roots.push(node);
      }
    }

    const sortBranch = (nodes: CommentThreadNode[]): void => {
      nodes.sort(
        (a, b) =>
          this.compareCommentDates(a.comment.created_at, b.comment.created_at)
      );
      nodes.forEach((n) => sortBranch(n.replies));
    };
    sortBranch(roots);

    return roots;
  }

  private compareCommentDates(a: string, b: string): number {
    return this.parseCommentDate(a) - this.parseCommentDate(b);
  }

  private parseCommentDate(raw: string): number {
    if (!raw?.trim()) {
      return 0;
    }
    const trimmed = raw.trim();
    if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
      return new Date(iso).getTime();
    }
    return new Date(trimmed.replace(' ', 'T') + 'Z').getTime();
  }

  /**
   * Get the display name for reply target
   */
  getReplyTargetName(): string {
    if (!this.replyToComment) return '';
    return this.commentsService.getUserDisplayName(this.replyToComment.created_by_user);
  }

  /**
   * Get comment count text
   */
  getCommentCountText(): string {
    const count = this.comments.length;
    return `${count} ${count === 1 ? 'Comment' : 'Comments'}`;
  }

  /** Used by template for send button — p-editor can bind `null` when empty. */
  hasNewCommentEditorText(): boolean {
    return !!this.trimmedEditorHtml(this.newCommentText);
  }

  hasReplyEditorText(): boolean {
    return !!this.trimmedEditorHtml(this.replyText);
  }

  private trimmedEditorHtml(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  onEditorTextChange(_event: unknown, _type: 'comment' | 'reply'): void {
    // Reserved for future editor-specific handling (e.g. analytics).
  }
}
